/**
 * @file file-upload.ts
 * @description OSS 直傳上傳助手（Task 25；批量/壓縮包上傳見 uploadFiles / expandZipFiles）。
 *
 * 實現三步驟直傳流程：
 *   1. 向後端請求 OSS 預簽名上傳 URL（POST /api/files/presign）
 *   2. 前端直接以 PUT 方式上傳文件到 OSS（支持上傳進度回調）
 *   3. 上傳完成後通知後端落庫（POST /api/files/confirm）
 *
 * 使用 XHR 而非 fetch 以支持 upload.onprogress 回調。
 *
 * 批量能力（客戶一次上傳大量文件，如集團驗證 ~81 份土地謄本）：
 *   - uploadFiles()：併發池（預設 3）逐檔 presign→PUT，最後批量 confirm 落庫，
 *     單檔失敗不中斷其餘，返回 { succeeded, failed }。
 *   - expandZipFiles()：瀏覽器端解開 .zip 壓縮包（跳過目錄 / __MACOSX / 隱藏檔），
 *     非 zip 檔原樣通過，供上傳點在進池前先展開。
 */

import { apiClient } from '@/lib/api-client';

/** 文件業務分類 */
export type FileScope =
  | 'case'
  | 'form-template'
  | 'certificate'
  | 'archive'
  | 'avatar'
  | 'misc'
  | 'form-attachment'
  | 'staff-doc'
  | 'signature'
  | 'document-library';

/** 上傳成功後返回的文件記錄（對應後端 files 表） */
export interface UploadedFile {
  /** 資料庫主鍵 */
  id: number;
  /** OSS 對象 key，用於後端生成下載連結 */
  ossKey: string;
  /** 原始文件名 */
  filename: string;
  /** 文件大小（位元組） */
  sizeBytes: number;
  /** MIME 類型 */
  mimeType: string;
}

/** 預簽名上傳 URL 後端返回結構 */
interface PresignedUpload {
  ossKey: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresInSec: number;
}

/** confirm 端點後端返回結構（單筆形式） */
interface ConfirmResponse {
  file: UploadedFile;
}

/** confirm 端點後端返回結構（批量形式：請求體為陣列時） */
interface ConfirmBatchResponse {
  files: UploadedFile[];
}

/** POST /api/files/confirm 單筆請求體 */
interface ConfirmPayload {
  ossKey: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  scope: FileScope;
  ownerType?: string;
  ownerId?: number;
  /** 業務專屬附加資訊（jsonb，後端原樣落庫），如文件資料庫的 scheme/description/isPublic */
  meta?: Record<string, unknown>;
}

/** 單檔上限 100MB，與後端 presign 的 FILE_TOO_LARGE 校驗保持一致（前端提前攔截省一次往返） */
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

/** 批量 confirm 單次請求最大筆數（與後端 confirm 批量 schema 的 max 保持一致） */
const CONFIRM_BATCH_CHUNK_SIZE = 100;

/** uploadFile 選項 */
export interface UploadFileOptions {
  /** 業務分類，決定 OSS 存儲路徑前綴 */
  scope: FileScope;
  /** 所屬業務類型（如 'case'、'client'），可選 */
  ownerType?: string;
  /** 所屬業務記錄 ID，可選 */
  ownerId?: number;
  /**
   * 上傳進度回調，參數 pct 為 0–100 的百分比。
   * 僅在瀏覽器 XHR upload 支持 lengthComputable 時才有效。
   */
  onProgress?: (pct: number) => void;
  /** 当 file 是 Blob（无 .name/.type）时显式传入 */
  filename?: string;
  /** 当 file 是 Blob 时显式传入 mime */
  mimeType?: string;
  /** 業務專屬附加資訊（jsonb，後端原樣落庫），如文件資料庫的 scheme/description/isPublic */
  meta?: Record<string, unknown>;
}

/**
 * 直傳 OSS 上傳文件。
 *
 * 步驟：
 *   1. 向後端請求預簽名 URL（含有效期 5 分鐘）
 *   2. 直接 PUT 到 OSS（bypasses 後端，節省流量）
 *   3. 通知後端確認上傳並落庫，返回文件記錄
 *
 * @param file 要上傳的 File 對象（來自 input[type=file] 或拖拽）
 * @param opts 上傳選項（業務分類、所有者信息、進度回調）
 * @returns 持久化後的文件記錄
 *
 * @example
 * ```ts
 * const uploaded = await uploadFile(file, {
 *   scope: 'case',
 *   ownerType: 'case',
 *   ownerId: caseId,
 *   onProgress: (pct) => setProgress(pct),
 * });
 * console.log('file id:', uploaded.id);
 * ```
 */
export async function uploadFile(
  file: File | Blob,
  opts: UploadFileOptions,
): Promise<UploadedFile> {
  // Blob 没有 .name；当来源是 Blob（如 IndexedDB 暂存的离线 Blob）时由 opts 显式传入
  const filename = opts.filename ?? (file as File).name ?? 'unknown';
  const mimeType = opts.mimeType ?? file.type ?? 'application/octet-stream';

  // Step 1：向後端請求預簽名上傳 URL
  const presign = await apiClient.post<PresignedUpload>('/api/files/presign', {
    scope: opts.scope,
    filename,
    mimeType,
    sizeBytes: file.size,
  });

  // Step 2：使用 XHR PUT 直傳 OSS，以支持 upload.onprogress 進度回調
  await putToOss(file, presign, opts.onProgress);

  // Step 3：通知後端確認上傳完成，落庫元數據
  const confirmed = await apiClient.post<ConfirmResponse>('/api/files/confirm', {
    ossKey: presign.ossKey,
    filename,
    mimeType,
    sizeBytes: file.size,
    scope: opts.scope,
    ownerType: opts.ownerType,
    ownerId: opts.ownerId,
    meta: opts.meta,
  } satisfies ConfirmPayload);

  return confirmed.file;
}

/**
 * 以 XHR PUT 直傳單一文件到 OSS 預簽名 URL。
 *
 * 使用 XHR 而非 fetch 的原因：fetch 目前無法取得 upload progress；
 * XHR 的 upload.onprogress 可回報 0–100 的上傳百分比。
 *
 * @param file 要上傳的 File / Blob
 * @param presign 後端返回的預簽名信息（uploadUrl + 必帶 headers）
 * @param onProgress 單檔上傳進度回調（0–100）
 */
function putToOss(
  file: File | Blob,
  presign: PresignedUpload,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl);

    // 設置後端要求的請求頭（通常包含 Content-Type）
    Object.entries(presign.headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    // 進度回調：只有當瀏覽器能計算總量時才觸發
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.((event.loaded / event.total) * 100);
      }
    };

    // 上傳完成：2xx 視為成功
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`OSS PUT failed with status ${xhr.status}`));
      }
    };

    // 網絡錯誤
    xhr.onerror = () => reject(new Error('OSS upload network error'));

    xhr.send(file);
  });
}

/* ============================================================
 * 批量上傳（uploadFiles）
 * ============================================================ */

/** 批量上傳中單一文件的失敗記錄 */
export interface UploadFailure {
  /** 失敗的原始 File */
  file: File;
  /** 失敗原因（presign / PUT / confirm 任一步驟拋出的錯誤） */
  error: Error;
}

/** uploadFiles 的返回結構：成功與失敗分列，單檔失敗不影響其餘 */
export interface UploadFilesResult {
  /** 已成功落庫的文件記錄（順序與完成順序相關，不保證與輸入一致） */
  succeeded: UploadedFile[];
  /** 失敗清單（含失敗原因，供上傳點提示用戶重傳） */
  failed: UploadFailure[];
}

/** uploadFiles 選項 */
export interface UploadFilesOptions {
  /** 業務分類，決定 OSS 存儲路徑前綴 */
  scope: FileScope;
  /** 所屬業務類型（如 'case'、'client'），可選 */
  ownerType?: string;
  /** 所屬業務記錄 ID，可選 */
  ownerId?: number;
  /** 併發池大小（同時進行 presign+PUT 的文件數），預設 3 */
  concurrency?: number;
  /** 業務專屬附加資訊（jsonb，後端原樣落庫），套用到本批所有文件 */
  meta?: Record<string, unknown>;
  /**
   * 批量進度回調：每完成一個文件（成功或失敗均計數）觸發一次。
   * @param done 已完成（含失敗）的文件數
   * @param total 總文件數
   * @param current 剛完成的文件名
   */
  onProgress?: (done: number, total: number, current: string) => void;
}

/**
 * 通用併發池：以固定上限併發執行 worker，直到所有 items 處理完。
 *
 * worker 內部應自行捕獲業務錯誤（本函數不吞錯——任一 worker 拋錯會讓整池 reject），
 * 批量上傳等「單項失敗不中斷」場景請在 worker 裡 try/catch。
 *
 * @param items 待處理項目
 * @param limit 併發上限（自動夾在 1..items.length）
 * @param worker 單項處理函數
 */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;
  // 用共享游標讓每個 worker 取下一個待處理項，實現「先完成先補位」的池化行為
  let cursor = 0;
  const poolSize = Math.max(1, Math.min(limit, items.length));
  await Promise.all(
    Array.from({ length: poolSize }, async () => {
      for (;;) {
        const index = cursor;
        cursor += 1;
        if (index >= items.length) return;
        await worker(items[index]!, index);
      }
    }),
  );
}

/**
 * 批量直傳 OSS 上傳多個文件。
 *
 * 流程：
 *   1. 併發池（預設 3）逐檔執行 presign → PUT 直傳 OSS；
 *      單檔失敗（含超過 100MB 的前端預檢）記入 failed，不中斷其餘文件。
 *   2. 全部 PUT 完成後，以「批量 confirm」一次落庫多筆元數據
 *      （POST /api/files/confirm 傳陣列，減少 81 檔 81 次往返的開銷）；
 *      整批 confirm 請求失敗時自動回退為逐筆 confirm，保住已直傳成功的文件。
 *
 * @param files 要上傳的文件列表（通常先經 expandZipFiles 展開）
 * @param opts 上傳選項（scope/owner/併發數/批量進度回調）
 * @returns { succeeded, failed } 成功與失敗分列
 *
 * @example
 * ```ts
 * const files = await expandZipFiles(pickedFiles);
 * const { succeeded, failed } = await uploadFiles(files, {
 *   scope: 'case',
 *   ownerType: 'case',
 *   ownerId: caseId,
 *   onProgress: (done, total, name) => setStatus(`${done}/${total} ${name}`),
 * });
 * ```
 */
export async function uploadFiles(
  files: File[],
  opts: UploadFilesOptions,
): Promise<UploadFilesResult> {
  const total = files.length;
  const succeeded: UploadedFile[] = [];
  const failed: UploadFailure[] = [];
  /** presign+PUT 已完成、等待批量 confirm 落庫的文件 */
  const pendingConfirms: { file: File; payload: ConfirmPayload }[] = [];
  let done = 0;

  // ── 階段 1：併發池逐檔 presign → PUT ────────────────────────────────────────
  await runWithConcurrency(files, opts.concurrency ?? 3, async (file) => {
    try {
      // 前端預檢：超過 100MB 直接失敗，省去必然被後端 413 拒絕的 presign 往返
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`「${file.name}」超過單檔 100MB 上限`);
      }

      const filename = file.name || 'unknown';
      const mimeType = file.type || 'application/octet-stream';

      const presign = await apiClient.post<PresignedUpload>('/api/files/presign', {
        scope: opts.scope,
        filename,
        mimeType,
        sizeBytes: file.size,
      });

      await putToOss(file, presign);

      pendingConfirms.push({
        file,
        payload: {
          ossKey: presign.ossKey,
          filename,
          mimeType,
          sizeBytes: file.size,
          scope: opts.scope,
          ownerType: opts.ownerType,
          ownerId: opts.ownerId,
          meta: opts.meta,
        },
      });
    } catch (err) {
      // 業務必要：單檔上傳失敗需輸出錯誤日誌（單檔失敗不中斷其餘文件）
      console.error('[file-upload] uploadFiles single file failed', { filename: file.name, err });
      failed.push({ file, error: err instanceof Error ? err : new Error(String(err)) });
    } finally {
      done += 1;
      opts.onProgress?.(done, total, file.name);
    }
  });

  // ── 階段 2：批量 confirm 落庫（chunk ≤ 100，與後端批量 schema 上限一致） ────
  for (let i = 0; i < pendingConfirms.length; i += CONFIRM_BATCH_CHUNK_SIZE) {
    const chunk = pendingConfirms.slice(i, i + CONFIRM_BATCH_CHUNK_SIZE);
    try {
      const res = await apiClient.post<ConfirmBatchResponse>(
        '/api/files/confirm',
        chunk.map((c) => c.payload),
      );
      succeeded.push(...res.files);
    } catch (batchErr) {
      // 業務必要：批量 confirm 失敗需輸出錯誤日誌；回退逐筆 confirm 以保住已直傳成功的文件
      console.error('[file-upload] batch confirm failed, falling back to per-file confirm', {
        count: chunk.length,
        err: batchErr,
      });
      for (const c of chunk) {
        try {
          const single = await apiClient.post<ConfirmResponse>('/api/files/confirm', c.payload);
          succeeded.push(single.file);
        } catch (err) {
          // 業務必要：逐筆 confirm 兜底也失敗，記錄並歸入 failed
          console.error('[file-upload] per-file confirm fallback failed', {
            filename: c.file.name,
            err,
          });
          failed.push({ file: c.file, error: err instanceof Error ? err : new Error(String(err)) });
        }
      }
    }
  }

  return { succeeded, failed };
}

/* ============================================================
 * zip 壓縮包展開（expandZipFiles）
 * ============================================================ */

/** 常見 zip MIME 類型（瀏覽器 / OS 差異：Windows 常見 x-zip-compressed） */
const ZIP_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip',
]);

/** 依副檔名判斷是否為 zip 壓縮包（MIME 為輔，避免 OS 未註冊 MIME 時漏判） */
function isZipFile(file: File): boolean {
  return /\.zip$/i.test(file.name) || ZIP_MIME_TYPES.has(file.type);
}

/**
 * zip 內文件副檔名 → MIME 推斷表。
 * 解壓出的 Blob 沒有 MIME 信息，按副檔名補齊常見類型，其餘回落 octet-stream。
 */
const EXT_MIME_MAP: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
};

/**
 * 判斷 zip 內部條目是否為應跳過的隱藏/系統文件：
 *   - macOS 壓縮產生的 __MACOSX/ 資源目錄
 *   - 任一路徑段以 '.' 開頭（.DS_Store、.git、隱藏檔等）
 */
function isHiddenZipEntry(entryPath: string): boolean {
  return entryPath.split('/').some((seg) => seg === '__MACOSX' || seg.startsWith('.'));
}

/**
 * 在瀏覽器端展開 zip 壓縮包：
 *   - .zip 文件 → 解壓為多個 File（跳過目錄項、__MACOSX、.DS_Store 等隱藏文件；
 *     zip 內子目錄的文件取其原始檔名（basename），不保留目錄層級；
 *     嵌套 zip 不遞歸展開，按普通文件原樣輸出）
 *   - 非 zip 文件 → 原樣通過
 *
 * 損壞/無法解析的 zip 直接拋錯（帶檔名），由呼叫端提示用戶後中止本次批量。
 *
 * @param files 用戶選取的文件列表（可混合 zip 與一般文件）
 * @returns 展開後的文件列表（保持選取順序：zip 展開項插在原 zip 的位置）
 */
export async function expandZipFiles(files: File[]): Promise<File[]> {
  const out: File[] = [];

  for (const file of files) {
    if (!isZipFile(file)) {
      out.push(file);
      continue;
    }

    // 動態 import：只有真的遇到 zip 才載入 jszip，不佔首屏 bundle
    const JSZip = (await import('jszip')).default;

    let zip: import('jszip');
    try {
      zip = await JSZip.loadAsync(await file.arrayBuffer());
    } catch (err) {
      // 業務必要：解壓失敗需輸出錯誤日誌
      console.error('[file-upload] expandZipFiles unzip failed', { filename: file.name, err });
      throw new Error(`無法解壓縮「${file.name}」，請確認壓縮包是否損壞`, { cause: err });
    }

    for (const entry of Object.values(zip.files)) {
      // 跳過目錄項與隱藏/系統文件
      if (entry.dir || isHiddenZipEntry(entry.name)) continue;

      const buffer = await entry.async('arraybuffer');
      // 保留原始檔名（不含 zip 內目錄前綴）
      const basename = entry.name.split('/').pop() ?? entry.name;
      const dotIdx = basename.lastIndexOf('.');
      const ext = dotIdx >= 0 ? basename.slice(dotIdx + 1).toLowerCase() : '';
      out.push(
        new File([buffer], basename, {
          type: EXT_MIME_MAP[ext] ?? 'application/octet-stream',
          lastModified: entry.date?.getTime() ?? Date.now(),
        }),
      );
    }
  }

  return out;
}
